import api from '@/services/api';
import type { PaginatedResponse } from '@/types/api';
import type {
  PatientLtv,
  CampaignLtv,
  ChannelLtv,
  ProcedureLtv,
  LtvDashboard,
  LtvTier,
} from '@/types/ltv';

const BASE_PATH = '/ltv';

/**
 * LTV (Lifetime Value) analytics API service.
 */
export const ltvService = {
  /**
   * Lists patient LTV records with pagination and optional filters.
   */
  async getPatients(params?: {
    page?: number;
    limit?: number;
    tier?: LtvTier;
    sortBy?: string;
  }): Promise<PaginatedResponse<PatientLtv>> {
    const { data } = await api.get<PaginatedResponse<PatientLtv>>(
      `${BASE_PATH}/patients`,
      { params },
    );
    return data;
  },

  /**
   * Gets LTV detail for a single patient.
   */
  async getPatientDetail(patientId: string): Promise<PatientLtv> {
    const { data } = await api.get<PatientLtv>(
      `${BASE_PATH}/patients/${patientId}`,
    );
    return data;
  },

  /**
   * Gets LTV aggregations by campaign.
   */
  async getCampaigns(): Promise<CampaignLtv[]> {
    const { data } = await api.get<CampaignLtv[]>(`${BASE_PATH}/campaigns`);
    return data;
  },

  /**
   * Gets LTV aggregations by acquisition channel.
   */
  async getChannels(): Promise<ChannelLtv[]> {
    const { data } = await api.get<ChannelLtv[]>(`${BASE_PATH}/channels`);
    return data;
  },

  /**
   * Gets revenue aggregations by procedure type.
   */
  async getProcedures(): Promise<ProcedureLtv[]> {
    const { data } = await api.get<ProcedureLtv[]>(`${BASE_PATH}/procedures`);
    return data;
  },

  /**
   * Gets LTV dashboard data (top patients, at-risk, channel averages).
   */
  async getDashboard(): Promise<LtvDashboard> {
    const { data } = await api.get<LtvDashboard>(`${BASE_PATH}/dashboard`);
    return data;
  },
};
